<template>
	<div class="select-wrapper" tabindex="0" @focusout="changeDropdownState(false)" @keydown="keydownFn">
		<div v-if="label"><p class="txt-input-label">{{ label }}</p></div>
		<div class="select-element" :class="titleClass">
			<div class="select-title row centered space-between" @click="changeDropdownState(!open)">
				<div class="select-title-text"><p>{{ selectedName }}</p></div>
				<div>
					<AppChevronIcon :direction="chevronDirection"/>
				</div>
			</div>
			<AppDropdown
				:absolute="!relative"
				:collapsed="!this.open">
				<div ref="dropdown" class="scroll-container">
					<div class="select-dropdown-item" v-for="(item, index) in values" :key="index">
						<div :ref="'option-'+index"
							:class="{active: index === currentIndex}"
							class="select-option"
							@click="clickOption(index)">
							<p>{{ item }}</p>
						</div>
					</div>
				</div>
			</AppDropdown>
		</div>
	</div>
</template>

<script>
import AppChevronIcon from "@/shared/components/AppChevronIcon.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import {useLanguageStore} from "@/shared/stores";
export default {
	setup() {
		const languageStore = useLanguageStore();
		return {getString: languageStore.getString}
	},
	emits: ["selected", "toggling-dropdown", "toggled-dropdown"],
	name: "AppSelect",
	components: {AppChevronIcon, AppDropdown},
	data() {
		return {
			open: false,
			currentIndex: this.selectedIndex,
			moving: false,
		}
	},
	props: {
		label: {type: String, default: null},
		values: {type: Array, validator: (values) => {
			return values.every(value => {
				return typeof value === "string";
			});
		}},
		selectedIndex: {type: Number, default: null},
		relative: {type: Boolean, default: false},
	},
	methods: {
		changeDropdownState(newState) {
			if (!this.moving) {
				this.moving = true;
				this.open = newState;
				this.$emit("toggling-dropdown", newState);
				setTimeout(() => {
					this.moving = false;
					this.$emit("toggled-dropdown", newState);
				}, 300);
			}
		},
		clickOption(index) {
			this.changeDropdownState(false);
			this.currentIndex = index;
			this.$emit("selected", index, this.values[index]);
		},
		keydownFn(e) {
			if (e.keyCode === 40) {
				e.preventDefault();
				if(this.currentIndex === null) {
					this.currentIndex = 0;
				} else {
					this.$refs['option-'+this.currentIndex][0].classList.remove("active");
					this.currentIndex++;
				}
				if (this.currentIndex >= this.values.length) {
					this.currentIndex = 0;
				}
				this.$refs['option-'+this.currentIndex][0].classList.add("active");
				this.$refs['option-'+this.currentIndex][0].scrollIntoView({block: "nearest"});
				this.$emit("selected", this.currentIndex, this.values[this.currentIndex]);
			} else if (e.keyCode === 38) {
				e.preventDefault();
				if(this.currentIndex === null) {
					this.currentIndex = -1;
				} else {
					this.$refs['option-'+this.currentIndex][0].classList.remove("active");
					this.currentIndex--;
				}
				if (this.currentIndex < 0) {
					this.currentIndex = this.values.length - 1;
				}
				this.$refs['option-'+this.currentIndex][0].classList.add("active");
				this.$refs['option-'+this.currentIndex][0].scrollIntoView({block: "nearest"});
				this.$emit("selected", this.currentIndex, this.values[this.currentIndex]);
			} else if (e.keyCode === 13) {
				e.preventDefault();
				this.changeDropdownState(!this.open);
				this.$emit("selected", this.currentIndex, this.values[this.currentIndex]);
			}
		}
	},
	computed: {
		chevronDirection() {
			return this.open ? "up" : "down";
		},
		selectedName() {
			return this.currentIndex !== null ? this.values[this.currentIndex] : this.getString("select_placeholder");
		},
		titleClass() {
			return this.open && !this.relative ? "no-radius" : "";
		}
	},
	watch: {
		selectedIndex(newIndex) {
			this.currentIndex = newIndex;
		}
	},
}
</script>

<style lang="scss" scoped>
.select-wrapper {
	width: 100%;
	max-width: 250px;
	min-width: 150px;
	outline: none;
	.select-element {
		position: relative;
		box-shadow: 0 1px 2px var(--color-box-shadow-3);
		border-radius: 13px;
		transition: border-radius .1s $transition .25s;
		background-color: var(--color-bg);
		&.no-radius {
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
			transition: none;
		}
	}
	.select-title {
		@include noSelect;
		width: 100%;
		height: 40px;
		padding: 0 10px;
		cursor: pointer;
		.select-title-text {
			max-width: calc(100% - 16px);
			p {
				@include truncateString();
				font-size: 14px;
			}
		}
	}
	.scroll-container {
		max-height: 210px;
		overflow-y: auto;
		border-radius: 0 0 13px 13px;
		background-color: var(--color-bg-primary-2);
		&::-webkit-scrollbar {
			width: 4px;
			height: 4px;
			background-color: transparent;
		}
		&::-webkit-scrollbar-thumb {
			background-color: var(--color-fill-button);
			border-radius: 10px;
			width: 4px;
			height: 4px;
		}
		scrollbar-width: thin;
		scrollbar-color: var(--color-alpha-4);
	}
	:deep(.dropdown-container) {
		z-index: 101;
		border-radius: 0 0 13px 13px;
	}
	:deep(.dropdown-container .dropdown) {
		border-radius: 0 0 13px 13px;
	}
	:deep(.dropdown-container .dropdown .dropdown-content) {
		border-radius: 0 0 13px 13px;
		margin-top: 0;
		padding: 0;
		overflow: hidden;
	}
	:deep(.dropdown-container .absolute .dropdown) {
		border-radius: 0 0 13px 13px;
	}
	&:deep(.select-option) {
		@include centered;
		@include noSelect;
		padding: 5px;
		height: 35px;
		width: 100%;
		cursor: pointer;
		& * {
			@include noSelect;
			pointer-events: none;
		}
		p {
			@include truncateString();
			width: 100%;
			text-align: center;
			font-size: 12px;
			line-height: 15px;
		}
		&:hover {
			background-color: var(--color-fill-button-hover);
			p {
				color: var(--color-txt);
			}
		}
		&.active {
			background-color: var(--color-active-button);
			p {
				color: var(--color-txt);
			}
		}
		&:not(:last-of-type) {
			border-bottom: 1px solid var(--color-alpha-2);
		}
	}
}
</style>